// The store's follow-ups, shown in place of the style grid: customers to call
// now (follow-ups due or overdue, and wishes that are now in stock here),
// follow-ups planned for later, and the ones closed on this till. Mark one
// done once the customer has been called, with a word on how it went, or add a
// new follow-up for any customer.
// Online only: the customers' books are on the server. The server keeps no
// list of a store's closed follow-ups, so Done only shows this session's.
import React, { useEffect, useRef, useState } from "react";
import "./FashionWorkPanels.css";
import "./FashionFollowUpsPanel.css";
import { customerSearch, fashionPosService } from "../../../services";
import { readMessage } from "../../../utils/fashion/holds";
import { KIND_LABEL, buildAddClientItemMutation, buildCloseClientItemMutation, buildFollowUpsQuery, daysOverdue, dueLabel, itemTitle, readClientItems } from "../../../utils/fashion/clientBook";
import {
  DEFAULT_WHEN,
  FOLLOW_UP_TABS,
  REASONS,
  WHEN_CHOICES,
  buildCustomerLookupQuery,
  canLookUpCustomer,
  followUpStats,
  followUpsDueBy,
  initials,
  isFutureDue,
  readCustomerHits,
  splitFollowUps,
  telHref,
  validateFollowUpDraft,
} from "../../../utils/fashion/followUps";
import useFashionToast, { Toast } from "./hooks/useFashionToast";
import EmptyState from "./EmptyState";

export const LOOKUP_DELAY_MS = 300;

const pillTone = (item) => {
  if (item.closed) return item.closed === "DONE" ? "is-ok" : "is-neu";
  if (item.kind !== "TASK") return "is-info";
  const d = daysOverdue(item.dueDate);
  return d > 0 ? "is-bad" : d === 0 ? "is-warn" : "is-info";
};
const pillText = (item) => {
  if (item.closed) return item.closed === "DONE" ? "Done" : "Removed";
  return item.kind === "TASK" ? dueLabel(item.dueDate) : `${item.onHand ?? 0} in stock here`;
};

function AddFollowUp({ bunitId, say, onAdded }) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState({ status: "idle", rows: [] });
  const [customer, setCustomer] = useState(null);
  const [reason, setReason] = useState(REASONS[0]);
  const [when, setWhen] = useState(DEFAULT_WHEN);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  useEffect(() => {
    if (customer || !canLookUpCustomer(q)) {
      setHits({ status: "idle", rows: [] });
      return undefined;
    }
    let live = true;
    setHits((h) => ({ ...h, status: "loading" }));
    const timer = setTimeout(async () => {
      let rows = null;
      try {
        rows = readCustomerHits(await customerSearch(buildCustomerLookupQuery(q, bunitId)));
      } catch {
        rows = null;
      }
      if (live) setHits(rows === null ? { status: "error", rows: [] } : { status: "done", rows });
    }, LOOKUP_DELAY_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [q, customer, bunitId]);

  const pick = (c) => {
    setCustomer(c);
    setQ(c.name || c.mobileNo);
  };
  const change = () => {
    setCustomer(null);
    setQ("");
  };

  const save = async (e) => {
    e.preventDefault();
    if (savingRef.current) return;
    const { item, error } = validateFollowUpDraft({ bunitId, customer, reason, when, note });
    if (error) {
      say(error, "warn");
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      const res = readMessage(await fashionPosService(buildAddClientItemMutation(item)), "addFashionClientItem", "Couldn't add the follow-up. Try again.");
      if (!res.ok) {
        say(res.message, "bad");
        return;
      }
      say(`Follow-up added for ${customer.name || customer.mobileNo} · ${dueLabel(item.dueDate).toLowerCase()}.`, "ok");
      onAdded(item);
      setCustomer(null);
      setQ("");
      setNote("");
      setReason(REASONS[0]);
      setWhen(DEFAULT_WHEN);
    } catch {
      say("Couldn't add the follow-up. Check the connection and try again.", "bad");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <form className="fp-fu-form" onSubmit={save} aria-label="New follow-up">
      <h3 className="fp-wp-h">New follow-up</h3>
      <div className="fp-fu-fl">
        <label className="fp-wp-lbl" htmlFor="fp-fu-cust">
          Customer
        </label>
        {customer ? (
          <div className="fp-fu-picked">
            <span className="fp-wp-av" aria-hidden="true">
              {initials(customer.name)}
            </span>
            <span className="fp-fu-who">
              <b>{customer.name || "Customer"}</b>
              <span className="fp-wp-meta fp-num">{customer.mobileNo}</span>
            </span>
            <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={change}>
              Change
            </button>
          </div>
        ) : (
          <>
            <input id="fp-fu-cust" className="fp-input fp-num" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Mobile number or name" autoComplete="off" />
            {hits.status === "loading" && <span className="fp-wp-meta">Looking up…</span>}
            {hits.status === "error" && <span className="fp-wp-meta is-bad">Couldn't look up customers. Check the connection and try again.</span>}
            {hits.status === "done" && hits.rows.length === 0 && <span className="fp-wp-meta">No customer matches. Check the number or add them from the till.</span>}
            {hits.rows.length > 0 && (
              <ul className="fp-fu-hits" aria-label="Matching customers">
                {hits.rows.map((c) => (
                  <li key={c.customerId}>
                    <button type="button" className="fp-fu-hit" onClick={() => pick(c)}>
                      <b>{c.name || "Customer"}</b>
                      <span className="fp-wp-meta fp-num">{c.mobileNo}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
      <div className="fp-fu-fl">
        <span className="fp-wp-lbl" id="fp-fu-reason">
          Reason
        </span>
        <div className="fp-wp-seg is-wrap" role="group" aria-labelledby="fp-fu-reason">
          {REASONS.map((r) => (
            <button key={r} type="button" className={reason === r ? "is-on" : ""} aria-pressed={reason === r} onClick={() => setReason(reason === r ? "" : r)}>
              {r}
            </button>
          ))}
        </div>
      </div>
      <div className="fp-fu-fl">
        <span className="fp-wp-lbl" id="fp-fu-when">
          When
        </span>
        <div className="fp-wp-seg is-wrap" role="group" aria-labelledby="fp-fu-when">
          {WHEN_CHOICES.map((w) => (
            <button key={w.label} type="button" className={when === w.label ? "is-on" : ""} aria-pressed={when === w.label} onClick={() => setWhen(w.label)}>
              {w.label}
            </button>
          ))}
        </div>
      </div>
      <div className="fp-fu-fl">
        <label className="fp-wp-lbl" htmlFor="fp-fu-note">
          Note for whoever calls
        </label>
        <textarea id="fp-fu-note" className="fp-input fp-fu-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={400} placeholder="e.g. Rust wrap dress in M is back, hold till Saturday" />
      </div>
      <button type="submit" className="fp-btn-primary" disabled={saving}>
        {saving ? "Adding…" : "Add follow-up"}
      </button>
      {!customer && <span className="fp-wp-meta">Pick a customer first.</span>}
    </form>
  );
}

export default function FashionFollowUpsPanel({ tillData, isOnline, onClose, backLabel = "Back to styles" }) {
  const bunitId = tillData?.tillAccess?.csBunit?.csBunitId;
  const online = typeof isOnline === "boolean" ? isOnline : typeof navigator === "undefined" || navigator.onLine;
  const [list, setList] = useState({ status: "loading", rows: [] });
  const [done, setDone] = useState([]); // closed on this till since the screen opened
  const [tab, setTab] = useState("due");
  const [outcome, setOutcome] = useState({}); // itemId → what the customer said
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [reload, setReload] = useState(0);
  const [toast, say] = useFashionToast();

  useEffect(() => {
    if (!online || !bunitId) return undefined;
    let live = true;
    setList((l) => ({ ...l, status: "loading" }));
    (async () => {
      let rows = null;
      try {
        rows = readClientItems(await fashionPosService(buildFollowUpsQuery(bunitId, followUpsDueBy())), "getFashionFollowUps");
      } catch {
        rows = null;
      }
      if (live) setList((l) => (rows === null ? { status: "error", rows: l.rows } : { status: "done", rows }));
    })();
    return () => {
      live = false;
    };
  }, [online, bunitId, reload]);

  const close = async (item, status) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      const said = outcome[item.id];
      const res = readMessage(await fashionPosService(buildCloseClientItemMutation(item.id, status, said)), "closeFashionClientItem", "Couldn't update it.");
      if (res.ok) {
        say(`${item.customerName || "Customer"}: ${status === "DONE" ? "marked done" : "removed"}.`, "ok");
        setDone((d) => [{ ...item, closed: status, outcome: (said || "").trim() }, ...d.filter((x) => x.id !== item.id)]);
        setList((l) => ({ ...l, rows: l.rows.filter((r) => r.id !== item.id) }));
        setReload((n) => n + 1);
      } else {
        say(res.message, "bad");
      }
    } catch {
      say("Couldn't update it. Check the connection and try again.", "bad");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const added = (item) => {
    if (isFutureDue(item.dueDate)) setTab("up");
    else setTab("due");
    setReload((n) => n + 1);
  };

  const groups = splitFollowUps(list.rows);
  const shownByTab = { due: groups.due, up: groups.up, done };
  const shown = shownByTab[tab];
  const stats = followUpStats(list.rows, done);
  const empty = {
    due: "All caught up. No one to call today.",
    up: "Nothing planned. Add a follow-up on the right.",
    done: "Nothing closed yet on this till.",
  };

  const row = (item) => (
    <li key={item.id} className="fp-fu-row">
      <div className="fp-fu-top">
        <span className="fp-wp-av" aria-hidden="true">
          {initials(item.customerName)}
        </span>
        <span className="fp-fu-who">
          <b>{item.customerName || "Customer"}</b> {item.mobileNo && <span className="fp-wp-meta fp-num fp-fu-mob">{item.mobileNo}</span>}
          <span className="fp-wp-meta">
            {KIND_LABEL[item.kind]}
            {item.storeName ? ` · added at ${item.storeName}` : ""}
          </span>
        </span>
        <span className={`fp-wp-pill ${pillTone(item)}`}>{pillText(item)}</span>
      </div>
      <span className="fp-fu-text">{itemTitle(item)}</span>
      {item.closed ? (
        item.outcome && <span className="fp-wp-meta">“{item.outcome}”</span>
      ) : (
        <div className="fp-fu-acts">
          <input
            className="fp-input fp-fu-outcome"
            value={outcome[item.id] || ""}
            onChange={(e) => setOutcome((o) => ({ ...o, [item.id]: e.target.value }))}
            maxLength={255}
            placeholder="How did it go? (optional)"
            aria-label={`Outcome for ${item.customerName || "customer"}`}
          />
          {telHref(item.mobileNo) && (
            <a className="fp-btn-primary fp-btn-sm fp-fu-call" href={telHref(item.mobileNo)} aria-label={`Call ${item.customerName || "customer"}`}>
              Call
            </a>
          )}
          <button type="button" className={`${telHref(item.mobileNo) ? "fp-btn-ghost" : "fp-btn-primary"} fp-btn-sm`} onClick={() => close(item, "DONE")} disabled={busy}>
            Mark done
          </button>
          <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={() => close(item, "CAN")} disabled={busy} aria-label={`Remove ${itemTitle(item)} for ${item.customerName || "customer"}`}>
            Remove
          </button>
        </div>
      )}
    </li>
  );

  return (
    <section className="fp-panel fp-fu" aria-label="Follow-ups">
      <div className="fp-panel-head">
        <h2 className="fp-panel-title">Follow-ups</h2>
        <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={onClose}>
          {backLabel}
        </button>
      </div>
      <Toast toast={toast} />
      {!online ? (
        <EmptyState icon="offline" title="You're offline" hint="Follow-ups need a connection, because customers' books are on the server. Reconnect the till and try again." />
      ) : (
        <div className="fp-fu-split">
          <div className="fp-fu-main">
            <div className="fp-fu-stats fp-num" aria-label="Summary">
              <div>
                <span>Overdue</span>
                <b>{stats.over}</b>
              </div>
              <div>
                <span>Due today</span>
                <b>{stats.today}</b>
              </div>
              <div>
                <span>Upcoming</span>
                <b>{stats.up}</b>
              </div>
              <div>
                <span>Done</span>
                <b>{stats.done}</b>
              </div>
            </div>
            <div className="fp-wp-card fp-fu-card">
              <div className="fp-wp-head">
                <div className="fp-wp-seg is-scroll" role="group" aria-label="Show">
                  {FOLLOW_UP_TABS.map((t) => (
                    <button key={t.value} type="button" className={tab === t.value ? "is-on" : ""} aria-pressed={tab === t.value} onClick={() => setTab(t.value)}>
                      {`${t.label} · ${shownByTab[t.value].length}`}
                    </button>
                  ))}
                </div>
                <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={() => setReload((n) => n + 1)} disabled={list.status === "loading"}>
                  Refresh
                </button>
              </div>
              {list.status === "error" && (
                <p className="fp-wp-meta is-bad" role="alert">
                  Couldn't load the follow-ups. Check the connection and tap Refresh.
                </p>
              )}
              {tab === "done" && <p className="fp-wp-meta">Shows what was closed on this till since you opened Follow-ups.</p>}
              <ul className="fp-fu-list" aria-label={FOLLOW_UP_TABS.find((t) => t.value === tab).label} aria-busy={list.status === "loading" ? "true" : "false"}>
                {shown.length === 0 &&
                  list.status !== "error" &&
                  (list.status === "loading" && tab !== "done" ? <li className="fp-empty">Loading follow-ups…</li> : <EmptyState as="li" title={empty[tab]} />)}
                {shown.map(row)}
              </ul>
            </div>
          </div>
          <aside className="fp-fu-side" aria-label="Add follow-up">
            <AddFollowUp bunitId={bunitId} say={say} onAdded={added} />
          </aside>
        </div>
      )}
    </section>
  );
}
